import React from 'react'
import { Image, Platform } from 'react-native'
import { useNavigation } from '@react-navigation/native'
import Icon from 'react-native-vector-icons/Ionicons'
import styled, { useTheme } from 'styled-components/native'

import { getColourScheme } from 'APP/Helpers/AppearanceUtils'

// Services
import UrlBuilder from 'APP/Helpers/UrlBuilder'
import Analytics from 'APP/Services/Analytics'

// Components
import DialogueTouchableOpacity from 'APP/Components/DialogueTouchableOpacity'
import Typography from 'APP/Converse/Typography'

// Styles
import { Images } from 'APP/Themes'
import Styles from 'APP/Nav/style'

import { popToNative } from 'APP/BrownfieldBridge/BrownfieldBridgeInternal'
import { isAtFirstRoute } from 'APP/Nav'
import Button from 'APP/Converse/Button'
import I18n from 'APP/I18n'

const StyledButtonText = styled(Typography).attrs(() => ({
  variant: 'bodyNormal',
}))`
  ${Platform.select({
    android: `padding-left: ${({ theme }) => theme.metrics.baseMargin}px;`,
  })}
  ${({ disabled }) => disabled && 'opacity: 0.5;'}
`

/*** Base circular back button component - used internally by SafeBackButton and PopToNativeButton */
const CircularBackButton = ({ onPress, analyticsName, testID, accessibilityLabel, ...props }) => {
  return (
    <Button
      variant="tertiary"
      widthVariant="circle"
      icon={Images.backIcon}
      iconVariant="image"
      onPress={onPress}
      analyticsName={analyticsName}
      testID={testID}
      accessibilityLabel={accessibilityLabel}
      {...props}
    />
  )
}

export const HeaderButton = ({ image, icon, title, disabled, ...rest }) => {
  const theme = useTheme()

  return (
    <DialogueTouchableOpacity
      style={Styles.headerButton}
      disabled={disabled}
      analyticsName={title}
      {...rest}
    >
      {image && <Image source={image} />}
      {icon && (
        <Icon
          name={icon}
          style={Styles.buttonIcon}
          size={theme.fonts.fontSize.h2}
          color={theme.colors.text}
        />
      )}
      {title && !icon && <StyledButtonText disabled={disabled}>{title}</StyledButtonText>}
    </DialogueTouchableOpacity>
  )
}

export const HeaderModalCloseButton = ({ onClose, ...rest }) => {
  const navigation = useNavigation()
  return (
    <DialogueTouchableOpacity
      onPress={() => {
        if (typeof onClose === 'function') {
          onClose()
        }
        Analytics.trackEvent('button_click', { button_value: 'Back' })
        navigation.goBack()
      }}
      style={Styles.headerModalCloseButton}
      testID="BACK_BTN"
      analyticsName="Back"
      {...rest}
    >
      <Image style={Styles.headerModalCloseIcon} source={Images.closeIcon} />
    </DialogueTouchableOpacity>
  )
}

export const HeaderMentalHealthHelpButton = () => {
  const navigation = useNavigation()

  return (
    <HeaderButton
      icon="warning-outline"
      testID="MENTAL_HEALTH_HELP_BTN"
      onPress={() =>
        navigation.navigate('cmsScreen', {
          uri: UrlBuilder.cmsConsultUrl('emergencyCare', getColourScheme()),
          screen: 'cms',
          showShareButton: false,
        })
      }
    />
  )
}

/*** Safely renders a back button only when there's navigation history. Returns null if at the first route. */
export const SafeBackButton = (props = {}) => {
  const navigation = useNavigation()

  // Return no back button if there is no route to go back to
  if (isAtFirstRoute(navigation)) return null

  return (
    <CircularBackButton
      onPress={() => navigation.goBack()}
      analyticsName="Back"
      testID="BACK_BTN"
      accessibilityLabel={I18n.t('AccessibilityIcons.back') || ''}
      {...props}
    />
  )
}

/*** PopToNativeButton - Returns the user to the host app in embedded scenarios */
export const PopToNativeButton = (props = {}) => {
  return (
    <CircularBackButton
      onPress={() => popToNative(true)}
      analyticsName="PopToNative"
      testID="POP_TO_NATIVE_BTN"
      {...props}
    />
  )
}
